import { ActionButton, DataField, ErrorAlert, HighlightCallout, useDialog, useIfUser } from '@if/mui'
import { Badge, Divider, Stack, Tooltip } from '@mui/material'
import { useReplayCalendarActions } from '@src/api/loan/loan-api'
import { useDeleteMetro2Reporting, usePostMetro2Reporting } from '@src/api/loan/loan-metro2reporting'
import { transformMetro2ReportingToDto } from '@src/api/loan/loan-transformer'
import { getTimeNow } from '@src/services/DateTimeProvider'
import { Metro2Reporting } from '@src/types/loan/loan-calendar'
import { SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { CalendarCog, FileChartColumn, History, Pencil, Trash2 } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useEditMetro2ReportingDialog } from './EditMetro2ReportingDialog'
import { getFirstDayOfCurrentMonth } from './Metro2Reporting-schema'
import { useMetro2ReportingHistoryDialog } from './Metro2ReportingHistoryDialog'

export interface Metro2ReportingDialogProps {
  signedSimulation: SignedCalendarSimulation
}

/** The reporting the loan is on today: the most recently written one. */
const getCurrentMetro2Reporting = (metro2Reportings: Metro2Reporting[] | null): Metro2Reporting | null => {
  if (!metro2Reportings || metro2Reportings.length === 0) return null
  return metro2Reportings.reduce((prev, current) => (prev.createdOn > current.createdOn ? prev : current))
}

/**
 * Dropping the current override falls back to the last record that actually reached the
 * bureaus, not to nothing — what has been reported stays reported.
 */
const getPreviousReportedMetro2Reporting = (metro2Reportings: Metro2Reporting[]): Metro2Reporting | null => {
  if (!metro2Reportings || metro2Reportings.length === 0) return null
  return (
    metro2Reportings
      .filter((report) => report.reported)
      .sort((a, b) => b.metro2OverrideDate.getTime() - a.metro2OverrideDate.getTime())[0] ?? null
  )
}

/** A record is the agent's to drop only while its date is still in the current month or later. */
const canBeDeleted = (metro2Reporting: Metro2Reporting | null): boolean =>
  metro2Reporting !== null && metro2Reporting.metro2OverrideDate.getTime() >= getFirstDayOfCurrentMonth().getTime()

const useMetro2ReportingDialogState = ({ signedSimulation }: Metro2ReportingDialogProps) => {
  const { simulation } = signedSimulation
  const { loan, metro2Reportings } = simulation.fullLoan
  const user = useIfUser()
  const editDialog = useEditMetro2ReportingDialog()
  const {
    mutateAsync: postMetro2Reporting,
    isPending: isPosting,
    error: postError,
  } = usePostMetro2Reporting(loan.id, loan.financingCompanyId)
  const {
    mutateAsync: deleteMetro2Reporting,
    isPending: isDeleting,
    error: deleteError,
  } = useDeleteMetro2Reporting(loan.id, loan.financingCompanyId)
  const {
    mutateAsync: replayActions,
    isPending: isRefreshing,
    error: refreshError,
  } = useReplayCalendarActions(loan.id, loan.financingCompanyId)

  const historyDialog = useMetro2ReportingHistoryDialog()
  const savedReporting = useMemo(() => getCurrentMetro2Reporting(metro2Reportings), [metro2Reportings])
  const [reporting, setReporting] = useState(savedReporting)

  const showHistory = useCallback(
    () => void historyDialog.open({ reportings: metro2Reportings }),
    [historyDialog, metro2Reportings],
  )

  const edit = useCallback(async () => {
    const values = reporting ? await editDialog.edit(reporting) : await editDialog.create()
    if (!values) return

    setReporting({
      id: null,
      reported: false,
      createdOn: getTimeNow(),
      createdByUserFullname: user.name,
      createdByUserId: user.id,
      ...values,
    })
  }, [editDialog, reporting, user])

  const remove = useCallback(() => {
    setReporting(getPreviousReportedMetro2Reporting(metro2Reportings))
  }, [metro2Reportings])

  const save = useCallback(async (): Promise<SignedCalendarSimulation | false> => {
    try {
      if (savedReporting && !reporting) {
        await deleteMetro2Reporting()
      } else if (reporting !== savedReporting) {
        await postMetro2Reporting(transformMetro2ReportingToDto(reporting!))
      } else {
        return false
      }

      return await replayActions({ simulation, actions: simulation.actions })
    } catch {
      return false
    }
  }, [deleteMetro2Reporting, postMetro2Reporting, replayActions, reporting, savedReporting, simulation])

  return {
    reporting,
    history: metro2Reportings,
    edit,
    remove,
    save,
    showHistory,
    canDelete: canBeDeleted(reporting),
    hasChanges: reporting !== savedReporting,
    isPending: isPosting || isDeleting || isRefreshing,
    error: postError ?? deleteError ?? refreshError,
  }
}

type Metro2ReportingDialogState = ReturnType<typeof useMetro2ReportingDialogState>

/**
 * Shows what the loan reports to the bureaus today and everything it has reported before, and
 * changes the current record through a form dialog stacked over this one.
 */
export const useMetro2ReportingDialog = () => {
  const { t } = useTranslation()

  return useDialog<Metro2ReportingDialogProps, SignedCalendarSimulation, Metro2ReportingDialogState | undefined>({
    icon: FileChartColumn,
    title: t('loan.payment.creditReportDialog.title'),
    subtitle: t('loan.payment.creditReportDialog.descriptionDialog'),
    confirmLabel: t('common.save'),
    maxWidth: 'md',
    fullWidth: true,
    useDialogState: useMetro2ReportingDialogState,

    additionalActions: (ctx) => {
      const state = ctx.state
      if (!state) return null
      return (
        <Stack direction="row" spacing={1}>
          <ActionButton startIcon={Pencil} onClick={() => void state.edit()} disabled={state.isPending}>
            {state.reporting
              ? t('loan.payment.creditReportDialog.editDate')
              : t('loan.payment.creditReportDialog.addDate')}
          </ActionButton>
          <Tooltip title={state.canDelete ? '' : t('loan.payment.creditReportDialog.deleteDisabled')}>
            <span>
              <ActionButton
                tone="destructive"
                startIcon={Trash2}
                onClick={state.remove}
                disabled={!state.canDelete || state.isPending}
              >
                {t('common.delete')}
              </ActionButton>
            </span>
          </Tooltip>
          <Badge badgeContent={state.history.length} color="primary">
            <ActionButton startIcon={History} onClick={state.showHistory}>
              {t('loan.payment.creditReportDialog.history')}
            </ActionButton>
          </Badge>
        </Stack>
      )
    },

    confirmLoading: (ctx) => Boolean(ctx.state?.isPending),

    confirmDisabled: (ctx) => !ctx.state?.hasChanges,

    confirmTooltip: (ctx) =>
      ctx.state?.hasChanges ? t('common.save') : t('loan.payment.creditReportDialog.nothingToConfirm'),

    content: (ctx) => {
      const state = ctx.state
      if (!state) return null
      const { reporting } = state
      return (
        <Stack spacing={2}>
          {state.error && <ErrorAlert error={state.error} disabledGutterBottom />}
          <HighlightCallout
            icon={CalendarCog}
            subjectLabel={t('loan.payment.creditReportDialog.reason')}
            subject={reporting ? t(`enum.EMetro2ReportingReason.${reporting.metro2ReportingReason}`) : t('common.none')}
            valueLabel={t('loan.payment.creditReportDialog.date')}
            value={reporting ? reporting.metro2OverrideDate.toString() : t('common.none')}
          />
          <Stack spacing={1.5} divider={<Divider />}>
            <DataField label={t('loan.payment.creditReportDialog.desc')} value={reporting?.description} />
            <Stack direction="row" spacing={3}>
              <DataField
                label={t('loan.payment.creditReportDialog.createdBy')}
                value={reporting && (reporting.createdByUserFullname || t('loan.payment.creditReportDialog.system'))}
                sx={{ flex: '1 1 50%', minWidth: 0 }}
              />
              <DataField
                label={t('loan.payment.creditReportDialog.createdOn')}
                value={reporting?.createdOn.toLocaleString()}
                sx={{ flex: '1 1 50%', minWidth: 0 }}
              />
            </Stack>
          </Stack>
        </Stack>
      )
    },

    onConfirm: (ctx) => ctx.state?.save() ?? false,
  })
}
